// The READ-TIME contract: what a behaviour's emissions must mean, regardless of
// what produced them.
//
// The factory is growing a second implementation tier — behaviours persisted as
// executable artifacts with full authority over a trace's turn windows — and the
// containment moves accordingly. Generation is free (an agent with tools, batch
// cycle); the artifact is free (sandboxed code, not a DSL); what is BOUND is the
// emission: every occurrence a behaviour claims must be named and structured so
// the dashboard and the behaviour graph can consume it without reading the
// implementation. Closed keys, open values.
//
// Two shapes, validated separately because they fail differently:
//
//   MANIFEST   the behaviour as a graph node type: identity, version, the
//              judgment metadata (consumer/decision/falsifier), the claim
//              names it will verdict on, and the field schema it emits.
//   OCCURRENCE the behaviour observed once: keyed to a tool_use block id,
//              carrying typed values, claim verdicts, and entity coordinates.
//
// Three invariants the graph depends on, enforced here rather than hoped for:
//
//   1. `key` is the tool_use block id. It is the residual accounting (what
//      fraction of a trace's work is claimed) AND the overlap join — two
//      behaviours claiming the same block is an edge, and it exists only if
//      everyone keys on the same atom.
//   2. Roles are closed, fields are open. A consumer knows `target` is an
//      entity, `metric` is a measurement, `evidence` is quotable proof — it
//      never needs to know what "verifier" means to file it correctly.
//   3. Every emission carries the artifact VERSION. A claim is attributable to
//      the code that made it, and a re-judged behaviour cannot silently
//      rewrite history.

import { skeletonKey } from "../mine/skeleton.js";

// What a field IS to a consumer that has never read the implementation:
//   target    the entity the act operated on — the graph's join candidate
//   metric    a measurement (number-valued)
//   evidence  a verbatim span a reader can quote against the claim
//   binding   a named value that is neither of the above (a flag, a mode)
export const FIELD_ROLES = Object.freeze(["target", "metric", "evidence", "binding"]);

// Host-stamped transport state of the underlying call — never claimed by the
// artifact, because "the call answered" is the host's knowledge, not the
// behaviour's. (The `call`-not-`outcome` lesson, kept at the contract level.)
export const CALL_STATES = Object.freeze(["answered", "errored", "unrecorded"]);

const ID_RE = /^[a-z][a-z0-9-]{1,31}$/;
const FIELD_RE = /^[a-z][a-zA-Z0-9_]{0,31}$/;
const MAX_CLAIMS = 8;
const MAX_FIELDS = 16;
const MAX_VALUE_CHARS = 4000;

/**
 * A deterministic content version for an artifact or spec.
 *
 * Not cryptographic — attribution and cache identity, not integrity. The
 * signed-manifest tier (shared-plugin-manifest) is where integrity lives when
 * artifacts eventually ship beyond the tenant that authored them.
 */
export function contentVersion(text) {
  return `fnv:${skeletonKey(String(text ?? ""))}`;
}

/**
 * Validate a behaviour manifest — the graph node type.
 *
 * @returns {{ok: boolean, problems: string[]}}
 */
export function validateBehaviourManifest(m) {
  const problems = [];
  const d = m && typeof m === "object" ? m : {};

  if (!ID_RE.test(d.behaviour || "")) {
    problems.push("behaviour: required, lowercase kebab, 2-32 chars — it names the graph node type");
  }
  if (!String(d.version || "").trim()) {
    problems.push("version: required — every emission must be attributable to the code that made it (use contentVersion)");
  }
  if (!String(d.name || "").trim()) problems.push("name: required — what an operator calls this behaviour");

  // The judgment metadata. Carried at the contract level so it cannot be
  // dropped between deciding a behaviour matters and indexing it: a behaviour
  // with no consumer is a data dump whatever its implementation.
  if (!String(d.consumer || "").trim()) problems.push("consumer: required — who reads this behaviour's occurrences");
  if (!String(d.decision || "").trim()) problems.push("decision: required — what that person decides differently");
  if (!String(d.falsifier || "").trim()) problems.push("falsifier: required — the observation that would show this behaviour wrongly named");

  // What the behaviour samples. Names for the tool door, a flag for the shell
  // door — the window down-selector needs this without running the artifact.
  const sub = d.substrate;
  if (!sub || typeof sub !== "object") {
    problems.push("substrate: required — {tools: [names], shell: bool}; the window is down-selected without running the artifact");
  } else {
    const tools = Array.isArray(sub.tools) ? sub.tools.filter((t) => typeof t === "string" && t.trim()) : [];
    if (!tools.length && sub.shell !== true) {
      problems.push("substrate: needs tool names and/or shell: true — a behaviour nothing samples is never observed");
    }
  }

  // Claim names are the declared probe surface. Implementations live in the
  // artifact; the NAMES live here so verdicts stay enumerable and support
  // stays measurable without reading code.
  const claims = d.claims;
  if (!Array.isArray(claims)) {
    problems.push("claims: required array (may be empty) — the named probes this behaviour verdicts on");
  } else {
    if (claims.length > MAX_CLAIMS) problems.push(`claims: more than ${MAX_CLAIMS} is a grammar, not a claim surface`);
    const seen = new Set();
    claims.forEach((c, i) => {
      const name = String(c?.name || "").trim();
      if (!name || name.length > 48) problems.push(`claims[${i}].name: required, <=48 chars`);
      if (seen.has(name)) problems.push(`claims[${i}].name: duplicate "${name}"`);
      seen.add(name);
    });
  }

  const emits = d.emits;
  const fieldNames = emits && typeof emits === "object" ? Object.keys(emits) : [];
  if (!fieldNames.length) {
    problems.push("emits: at least one field — an occurrence carrying nothing is a tally, not an observation");
  } else if (fieldNames.length > MAX_FIELDS) {
    problems.push(`emits: more than ${MAX_FIELDS} fields — an occurrence is an observation, not a row dump`);
  } else {
    for (const [name, f] of Object.entries(emits)) {
      if (!FIELD_RE.test(name)) problems.push(`emits.${name}: field names are lowerCamel, <=32 chars`);
      if (!f || typeof f !== "object" || !FIELD_ROLES.includes(f.role)) {
        problems.push(`emits.${name}: role must be one of ${FIELD_ROLES.join(", ")} — the closed vocabulary is what consumers file by`);
        continue;
      }
      if (f.role === "target") {
        // The identifiability declaration, promoted from a judge heuristic
        // (targetOutlivesSession) to a typed property: the graph merges
        // occurrences across traces ONLY on durable coordinates.
        if (typeof f.durable !== "boolean") {
          problems.push(`emits.${name}: a target must state durable: true|false — it decides whether the graph may merge on it`);
        }
        if (f.kind !== undefined && (typeof f.kind !== "string" || !f.kind.trim())) {
          problems.push(`emits.${name}: kind, when given, is a non-empty string (file, url, service, dataset, …)`);
        }
      }
    }
  }

  return { ok: problems.length === 0, problems };
}

const scalar = (v) => typeof v === "string" || typeof v === "number" || typeof v === "boolean";

/**
 * Validate one occurrence against its manifest.
 *
 * @returns {{ok: boolean, problems: string[]}}
 */
export function validateOccurrence(manifest, occ) {
  const problems = [];
  const m = manifest && typeof manifest === "object" ? manifest : {};
  const o = occ && typeof occ === "object" ? occ : {};

  if (o.behaviour !== m.behaviour) problems.push(`behaviour: "${o.behaviour}" does not match the manifest's "${m.behaviour}"`);
  if (o.version !== m.version) problems.push("version: does not match the manifest — emissions must be attributable to one artifact version");
  if (!String(o.key || "").trim()) {
    problems.push("key: required — the tool_use block id is the residual accounting and the graph's overlap join");
  }
  if (!CALL_STATES.includes(o.call)) {
    problems.push(`call: must be one of ${CALL_STATES.join(", ")} — host-stamped transport state, never claimed by the artifact`);
  }

  const emits = m.emits && typeof m.emits === "object" ? m.emits : {};
  const values = o.values && typeof o.values === "object" ? o.values : {};
  for (const [name, v] of Object.entries(values)) {
    if (!emits[name]) { problems.push(`values.${name}: not a declared field — the manifest is the schema`); continue; }
    if (!scalar(v)) problems.push(`values.${name}: values are scalars — structure belongs in more fields, not nested blobs`);
    else if (typeof v === "string" && v.length > MAX_VALUE_CHARS) problems.push(`values.${name}: over ${MAX_VALUE_CHARS} chars — an occurrence carries evidence, not payloads`);
    if (emits[name]?.role === "metric" && typeof v !== "number") {
      problems.push(`values.${name}: role metric requires a number`);
    }
  }

  const declared = new Set((Array.isArray(m.claims) ? m.claims : []).map((c) => String(c?.name || "").trim()));
  const claims = o.claims && typeof o.claims === "object" ? o.claims : {};
  for (const [name, v] of Object.entries(claims)) {
    if (!declared.has(name)) problems.push(`claims.${name}: not a declared claim — verdicts only exist under declared names`);
    if (typeof v !== "boolean") problems.push(`claims.${name}: a verdict is a boolean`);
  }

  if (o.entities !== undefined) {
    if (!Array.isArray(o.entities)) {
      problems.push("entities: must be an array of {kind, ref, durable}");
    } else {
      o.entities.forEach((e, i) => {
        if (!e || typeof e !== "object" || !String(e.kind || "").trim() || !String(e.ref || "").trim() || typeof e.durable !== "boolean") {
          problems.push(`entities[${i}]: needs kind, ref, and durable — a coordinate the graph can decide to merge on`);
        }
      });
    }
  }

  return { ok: problems.length === 0, problems };
}

/**
 * The boundary function a host calls on whatever an implementation produced.
 *
 * One malformed occurrence must not blank the batch — valid rows pass, invalid
 * ones are reported with their index and reasons. `invalid` is a THIRD failure
 * class beside "threw" and "empty": an artifact emitting malformed rows is a
 * different disease from one that crashed or one that matched nothing, and the
 * three must never print the same sentence.
 *
 * @returns {{manifestProblems: string[], valid: object[], invalid: {index: number, problems: string[]}[]}}
 */
export function validateEmissionBatch(manifest, occurrences) {
  const mCheck = validateBehaviourManifest(manifest);
  if (!mCheck.ok) return { manifestProblems: mCheck.problems, valid: [], invalid: [] };
  const valid = [];
  const invalid = [];
  (Array.isArray(occurrences) ? occurrences : []).forEach((occ, index) => {
    const r = validateOccurrence(manifest, occ);
    if (r.ok) valid.push(occ);
    else invalid.push({ index, problems: r.problems });
  });
  return { manifestProblems: [], valid, invalid };
}

/**
 * Derive entity coordinates from an occurrence's target-role values, for
 * implementations that emit values but not explicit entities. Explicit
 * entities win; this is the convenience path, not a second source of truth.
 */
export function entitiesFromValues(manifest, occ) {
  if (Array.isArray(occ?.entities) && occ.entities.length) return occ.entities;
  const out = [];
  const emits = manifest?.emits || {};
  for (const [name, f] of Object.entries(emits)) {
    if (f?.role !== "target") continue;
    const v = occ?.values?.[name];
    if (typeof v !== "string" || !v.trim()) continue;
    out.push({ kind: f.kind || "term", ref: v, durable: f.durable === true });
  }
  return out;
}
